const DATA = [
  { label: 'People', angle: -90, key: 'people' },
  { label: 'Organisation', angle: 30, key: 'organisations' },
  { label: 'Ecosystem', angle: 150, key: 'ecosystems' },
] as const;

type AxisKey = (typeof DATA)[number]['key'];

export type StakeholderRiskConcentrationRadarData = Record<
  AxisKey,
  { harmRank: string }
>;

export interface StakeholderRiskConcentrationRadarProps {
  data?: Partial<StakeholderRiskConcentrationRadarData> | null;
}

const LEVELS = 4; // max rank value is 4 (critical)
const SIZE = 420;
const CX = SIZE / 2;
const CY = SIZE / 2 + 10;
const MAX_R = 155;
const LABEL_R = MAX_R + 32;

const RISK_BANDS = [
  { scale: 1.0, fill: 'rgba(179,0,0,0.07)', stroke: 'rgba(179,0,0,0.50)' },
  { scale: 0.8, fill: 'rgba(239,102,9,0.07)', stroke: 'rgba(239,102,9,0.50)' },
  { scale: 0.6, fill: 'rgba(212,168,0,0.07)', stroke: 'rgba(212,168,0,0.50)' },
  { scale: 0.4, fill: 'rgba(46,139,87,0.07)', stroke: 'rgba(46,139,87,0.50)' },
  { scale: 0.2, fill: 'rgba(39,174,96,0.07)', stroke: 'rgba(39,174,96,0.50)' },
] as const;

const AXIS_STROKE = 'rgba(0,0,0,0.07)';
const LINE_COLOR = '#0D3D6B';
const SERIES_FILL = 'rgba(13,61,107,0.15)';

function getRiskValue(harmRank: string): number {
  switch (harmRank.toLowerCase()) {
    case 'critical':
      return 4;
    case 'high':
      return 3;
    case 'moderate':
      return 2;
    case 'low':
      return 1;
    default:
      return 1;
  }
}

function toXY(angleDeg: number, r: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function trianglePath(r: number): string {
  return (
    DATA.map((d, i) => {
      const { x, y } = toXY(d.angle, r);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ') + ' Z'
  );
}

function dataPath(values: readonly number[]): string {
  return (
    DATA.map((d, i) => {
      const { x, y } = toXY(d.angle, values[i]! * MAX_R);
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    }).join(' ') + ' Z'
  );
}

export default function StakeholderRiskConcentrationRadar({
  data,
}: StakeholderRiskConcentrationRadarProps) {
  const values = DATA.map((d) => {
    const rank = data?.[d.key]?.harmRank ?? 'low';
    return getRiskValue(rank) / LEVELS;
  });

  return (
    <div className="flex w-[380px] h-[280px] items-center justify-center [&_svg]:h-[280px] [&_svg]:w-[280px]">
      <svg
        // width={SIZE}
        // height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="overflow-visible"
        role="img"
        aria-label="Triangular radar chart with axes: People (top), Organisation (bottom right), Ecosystem (bottom left)"
      >
        {RISK_BANDS.map(({ scale, fill, stroke }, i) => {
          const r = MAX_R * scale;
          return (
            <path
              key={i}
              d={trianglePath(r)}
              fill={fill}
              stroke={stroke}
              strokeWidth={1}
            />
          );
        })}

        {DATA.map((d) => {
          const tip = toXY(d.angle, MAX_R);
          return (
            <line
              key={d.label}
              x1={CX}
              y1={CY}
              x2={tip.x}
              y2={tip.y}
              stroke={AXIS_STROKE}
              strokeWidth={1}
            />
          );
        })}

        <path
          d={dataPath(values)}
          fill={SERIES_FILL}
          stroke={LINE_COLOR}
          strokeWidth={2}
          strokeLinejoin="miter"
        />

        {DATA.map((d, i) => {
          const { x, y } = toXY(d.angle, values[i]! * MAX_R);
          return <circle key={d.label} cx={x} cy={y} r={6} fill={LINE_COLOR} />;
        })}

        {DATA.map((d) => {
          const pos = toXY(d.angle, LABEL_R);
          const anchor: 'middle' | 'start' | 'end' =
            d.angle === -90 ? 'middle' : d.angle === 30 ? 'start' : 'end';
          const dy = d.angle === -90 ? -4 : 6;
          return (
            <text
              key={d.label}
              x={pos.x}
              y={pos.y + dy}
              textAnchor={anchor}
              fontSize={15}
              fill="#1A1A18"
              fontWeight={400}
            >
              {d.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
